import type { OrderDetailsLineItem } from "@/domain/order/order-details.model"

/** Do not use raw backend IDs as React `key` — use {@link buildStableKey} from this module. */

/** Canonical type tags for React list keys (globally unique across sibling trees). */
export const REACT_STABLE_KEY_TYPE = {
  item: "item",
  modifier: "modifier",
  charge: "charge",
  discount: "discount",
  timeline: "timeline",
  orderMeta: "order-meta",
  ordersList: "orders-list",
  ordersListSkeleton: "orders-list-skeleton",
  addressCard: "address-card",
  addressSkeleton: "address-skeleton",
  promoOffer: "promo-offer",
  menuChefPick: "menu-chef-pick",
  menuChefPickSkeleton: "menu-chef-pick-skeleton",
  menuGridRow: "menu-grid-row",
  menuGridSkeleton: "menu-grid-skeleton",
  mealCombo: "meal-combo",
  mealTypeTab: "meal-type-tab",
  tagChip: "tag-chip",
  modifierGroup: "modifier-group",
  modifierOption: "modifier-option",
  comboGroup: "combo-group",
  comboOption: "combo-option",
  cartLine: "cart-line",
  cartAddon: "cart-addon",
  cartDrawerLine: "cart-drawer-line",
  cartRowTotal: "cart-row-total",
  cartPageTotal: "cart-page-total",
  checkoutTotal: "checkout-total",
  checkoutWireDiscount: "checkout-wire-discount",
  checkoutWireCharge: "checkout-wire-charge",
  orderFlowStep: "order-flow-step",
  couponRow: "coupon-row",
  categoryNav: "category-nav",
  miniProgress: "mini-progress",
  cartCount: "cart-count",
  bottomNav: "bottom-nav",
  checkoutProgressStep: "checkout-progress-step",
  filterQuickRow: "filter-quick-row",
  filterSheetGroup: "filter-sheet-group",
  filterSheetTag: "filter-sheet-tag",
  filterDrawerGroup: "filter-drawer-group",
  filterDrawerTag: "filter-drawer-tag",
  filterActiveChip: "filter-active-chip",
  quickFilterBarChip: "quick-filter-bar-chip",
} as const

/** UUID v4-ish (and common backend GUID) detector for dev warnings. */
const RAW_GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function warnIfRawGuidInParts(type: string, parts: Array<string | number | null | undefined>): void {
  if (process.env.NODE_ENV !== "development") return
  for (const p of parts) {
    if (p == null || typeof p === "number") continue
    const s = String(p).trim()
    if (RAW_GUID_RE.test(s)) {
      // eslint-disable-next-line no-console -- dev-only key hygiene
      console.warn(
        `[react-key] Raw GUID/UUID passed as key part for "${type}". Use opaqueIdFingerprint() or non-id fields:`,
        `${s.slice(0, 8)}…`
      )
    }
  }
}

/** Stable string for money/qty segments (avoids float drift in key identity). */
export function normalizeNumber(n?: number | null): string {
  if (n == null) return "0"
  const x = Number(n)
  return Number.isFinite(x) ? x.toFixed(2) : "0"
}

function normalizePart(part: string | number | null | undefined): string | null {
  if (part === null || part === undefined) return null
  const s = String(part).trim()
  return s === "" ? null : s
}

/**
 * Deterministic key segment from ordered parts. Null/empty parts are omitted.
 */
export function buildKey(...parts: Array<string | number | null | undefined>): string {
  const out: string[] = []
  for (const part of parts) {
    const n = normalizePart(part)
    if (n != null) out.push(n)
  }
  return out.join("|")
}

/** FNV-1a 32-bit — compact, deterministic, fast for React key bases. */
function hashKeyBase(input: string): string {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0).toString(16)
}

/**
 * Stable short fingerprint for an opaque backend id. **Do not** use the raw id as a React `key`;
 * pass this (or other domain fields) into {@link buildStableKey} instead.
 */
export function opaqueIdFingerprint(value: string | null | undefined): string {
  if (value == null) return "_"
  const s = String(value).trim()
  if (s === "") return "_"
  return hashKeyBase(s)
}

/**
 * Globally scoped list key: explicit `type`, hash of `type + payload` (payload excludes repeated type),
 * then sibling `index`. Output `type:hex#index` avoids cross-type hash collisions and eases debugging.
 */
export function buildStableKey(
  type: string,
  index: number,
  ...parts: Array<string | number | null | undefined>
): string {
  warnIfRawGuidInParts(type, parts)
  const base = buildKey(...parts)
  const payload = base.trim() === "" ? "unknown" : base
  const forHash = `${type}\u001f${payload}`
  return `${type}:${hashKeyBase(forHash)}#${index}`
}

/** Sorted modifier fingerprint for a line (order-independent). */
export function buildOrderLineModifierSignature(item: OrderDetailsLineItem): string {
  const segments = item.modifiers.map((m) =>
    buildKey(
      opaqueIdFingerprint(m.childMenuItemId ?? null),
      opaqueIdFingerprint(m.modifierGroupItemId ?? null),
      m.name,
      normalizeNumber(m.quantity),
      normalizeNumber(m.price),
      normalizeNumber(m.total)
    )
  )
  segments.sort()
  return segments.join(">")
}

/** Addon fingerprint for cart lines (no raw `addon_id`). */
export function cartLineAddonSignature(
  addons?: { addon_name?: string; addon_quantity?: number; addon_line_total?: number; addon_price?: number }[]
): string {
  if (!addons?.length) return ""
  const segments = addons.map((a) =>
    buildKey(a.addon_name, normalizeNumber(a.addon_quantity), normalizeNumber(a.addon_price), normalizeNumber(a.addon_line_total))
  )
  segments.sort()
  return segments.join(">")
}

export function buildCartLineStableKey(
  index: number,
  itemName: string,
  quantity: number,
  itemPrice: number,
  addons?: { addon_name?: string; addon_quantity?: number; addon_line_total?: number; addon_price?: number }[]
): string {
  return buildStableKey(
    REACT_STABLE_KEY_TYPE.cartLine,
    index,
    itemName,
    quantity,
    normalizeNumber(itemPrice),
    cartLineAddonSignature(addons)
  )
}

export function buildCartDrawerLineKey(
  index: number,
  itemName: string,
  quantity: number,
  lineTotal: number
): string {
  return buildStableKey(
    REACT_STABLE_KEY_TYPE.cartDrawerLine,
    index,
    itemName,
    quantity,
    normalizeNumber(lineTotal)
  )
}
